import { Position } from '../types';
import type { XYPosition } from '../types';
import { getStepRoute } from './stepRoute';

/** Corner radius of a `smoothstep` edge (v11 default). */
export const DEFAULT_STEP_BORDER_RADIUS = 5;

/** How far a step edge runs straight out of a handle before its first bend (v11 default). */
export const DEFAULT_STEP_OFFSET = 20;

const HALF = 0.5;

export type SmoothStepPathParams = {
  sourceX: number;
  sourceY: number;
  sourcePosition?: Position;
  targetX: number;
  targetY: number;
  targetPosition?: Position;
  /** 0 gives sharp corners (the `step` edge). */
  borderRadius?: number;
  offset?: number;
};

/** `[path, labelX, labelY, offsetX, offsetY]` — the v11 return shape. */
export type SmoothStepPath = [string, number, number, number, number];

const distance = (a: XYPosition, b: XYPosition): number => Math.hypot(b.x - a.x, b.y - a.y);

const towards = (from: XYPosition, to: XYPosition, length: number): XYPosition => {
  const total = distance(from, to);
  return { x: from.x + ((to.x - from.x) / total) * length, y: from.y + ((to.y - from.y) / total) * length };
};

/** A corner at `corner` rounded with a quadratic curve; never wider than half of either neighbouring segment. */
const roundedCorner = (previous: XYPosition, corner: XYPosition, next: XYPosition, borderRadius: number): string => {
  const radius = Math.min(borderRadius, distance(previous, corner) * HALF, distance(corner, next) * HALF);
  if (radius <= 0) return `L${corner.x} ${corner.y}`;
  const enter = towards(corner, previous, radius);
  const leave = towards(corner, next, radius);
  return `L${enter.x} ${enter.y}Q${corner.x} ${corner.y} ${leave.x} ${leave.y}`;
};

/** SVG path of a right-angled edge with rounded corners between two handles. */
export const getSmoothStepPath = ({
  sourceX,
  sourceY,
  sourcePosition = Position.Bottom,
  targetX,
  targetY,
  targetPosition = Position.Top,
  borderRadius = DEFAULT_STEP_BORDER_RADIUS,
  offset = DEFAULT_STEP_OFFSET,
}: SmoothStepPathParams): SmoothStepPath => {
  const points = getStepRoute({ x: sourceX, y: sourceY }, sourcePosition, { x: targetX, y: targetY }, targetPosition, offset);
  const path = points
    .map((point, index) => {
      if (index === 0) return `M${point.x} ${point.y}`;
      if (index === points.length - 1) return `L${point.x} ${point.y}`;
      return roundedCorner(points[index - 1], point, points[index + 1], borderRadius);
    })
    .join('');
  const labelX = (sourceX + targetX) * HALF;
  const labelY = (sourceY + targetY) * HALF;
  return [path, labelX, labelY, Math.abs(targetX - labelX), Math.abs(targetY - labelY)];
};
